import React, { useMemo } from 'react';
import { BasicContainer, BodyTextSmall, ButtonAsWrapper, FlexCol, FlexRow } from '@minecraft.atoms';
import { TabbedComponentProps, validateTabsProp } from './utils';

// border styles for the active tab on mobile
const activeMobileBorderStyle = {
  top: { borderColor: 'transparent' as const },
  left: { borderColor: 'transparent' as const },
  right: { borderColor: 'transparent' as const },
  bottom: { borderSize: '0.09', borderColor: 'emphasis' as const },
};

// border styles for the active tab on desktop, `{}` is the standard border 0.06rem solid with default border color
const activeDesktopBorderStyle = {
  top: {},
  left: {},
  right: {},
  bottom: { borderColor: 'transparent' as const },
};

// border styles for an inactive tab on mobile
const inactiveMobileBorderStyle = {
  top: { borderColor: 'transparent' as const },
  left: { borderColor: 'transparent' as const },
  right: { borderColor: 'transparent' as const },
  bottom: {},
};

// border styles for an inactive tab on desktop
const inactiveDesktopBorderStyle = {
  top: {},
  left: {},
  right: {},
  bottom: {},
};

/**
 * Tabbed Page takes a list of objects that describe each tab, as well as the active tab and a callback when a tab is clicked
 *
 * the tab buttons & tab panels are a11y friendly and provide a standard UI on mobile & desktop for the CN design system
 */
export const TabbedPage = <TabKey extends string>({
  activeTab,
  'data-qa-id': dataQaId,
  'data-testid': dataTestId,
  onTabChange,
  tabs,
}: TabbedComponentProps<TabKey>) => {
  const tabsToUse = useMemo(() => validateTabsProp({ tabs, componentName: 'TabbedPage' }), [tabs]);

  return (
    <FlexCol data-qa-id={dataQaId} data-testid={dataTestId}>
      <FlexRow
        maxHeight={3}
        role="tablist"
        smProps={{ mt: '8' }}
        className="cn_overflow-x-auto"
        alignItems="flex-end"
        wrap="nowrap"
        w="full"
      >
        <BasicContainer borderPlacement="bottom" w="6">
          &nbsp;
        </BasicContainer>
        {tabsToUse.map((tab) => {
          const isActive = tab.key === activeTab;
          return (
            <ButtonAsWrapper
              aria-controls={`${tab.key}-tabpanel`}
              aria-label={tab.tabTitle}
              aria-selected={isActive}
              data-qa-id={`${tab.key}-tab`}
              disabled={isActive}
              id={`${tab.key}-tabbutton`}
              key={tab.key}
              onClick={() => onTabChange(tab.key)}
              role="tab"
            >
              <BasicContainer
                key={tab.key}
                customBorders={isActive ? activeMobileBorderStyle : inactiveMobileBorderStyle}
                smProps={{
                  customBorders: isActive ? activeDesktopBorderStyle : inactiveDesktopBorderStyle,
                  customBordersRadius: { topLeft: 0.25, topRight: 0.25 },
                }}
                className="cn_atom_px-4 cn_atom_py-2"
              >
                <BodyTextSmall
                  noWrap
                  fontWeight={isActive ? 'semibold' : 'medium'}
                  color={isActive ? 'default' : 'primary'}
                >
                  {tab.tabTitle}
                </BodyTextSmall>
              </BasicContainer>
            </ButtonAsWrapper>
          );
        })}
        <FlexRow grow={4} borderPlacement="bottom" maxW="full" minWidth={1.5}>
          &nbsp;
        </FlexRow>
      </FlexRow>
      {tabsToUse.map((tab) => (
        <BasicContainer
          aria-labelledby={`${tab.key}-tabbutton`}
          className="cn_atom_px-8 cn_atom_py-6"
          hidden={tab.key !== activeTab}
          id={`${tab.key}-tabpanel`}
          key={tab.key}
          role="tabpanel"
          w="full"
        >
          {tab.Component}
        </BasicContainer>
      ))}
    </FlexCol>
  );
};
